import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth, api } from '../App';

const ALL_PLATFORMS = [
  { id: 'facebook',        label: 'Facebook',  color: '#1877f2', short: 'f',  desc: 'Story + Fragen'  },
  { id: 'instagram',       label: 'Instagram', color: '#e1306c', short: '◈',  desc: 'Hook + Hashtags' },
  { id: 'linkedin',        label: 'LinkedIn',  color: '#0077b5', short: 'in', desc: 'Professionell'   },
  { id: 'google_business', label: 'Google',    color: '#ea4335', short: 'G',  desc: 'Kurz + lokal'    },
  { id: 'x',               label: 'X',         color: '#555555', short: 'X',  desc: 'Kurz + direkt'   },
  { id: 'threads',         label: 'Threads',   color: '#888888', short: '@',  desc: 'Kurz + locker'   },
  { id: 'pinterest',       label: 'Pinterest', color: '#e60023', short: 'P',  desc: 'Visuell + SEO'   },
  { id: 'reddit',          label: 'Reddit',    color: '#ff4500', short: 'R',  desc: 'Community'       },
  { id: 'bluesky',         label: 'Bluesky',   color: '#0085ff', short: '☁',  desc: 'Offen + direkt'  },
];

const FALLBACK_PLATFORMS = ['facebook', 'instagram', 'linkedin', 'google_business'];
const TEXT_PLATS         = ['facebook','linkedin','x','threads','reddit','bluesky','google_business'];
const PHOTO_ONLY_PLATS   = ['instagram','pinterest'];
const IMAGE_STYLES       = [
  { id: 'professional', label: 'Professional', icon: '🏢' },
  { id: 'bold',         label: 'Bold',         icon: '⚡' },
  { id: 'minimal',      label: 'Minimal',      icon: '◻' },
  { id: 'warm',         label: 'Warm',         icon: '☀' },
];
const PLAT_INFO = {
  facebook:        { label: 'Facebook',  color: '#1877f2' },
  instagram:       { label: 'Instagram', color: '#e1306c' },
  linkedin:        { label: 'LinkedIn',  color: '#0077b5' },
  google_business: { label: 'Google',    color: '#ea4335' },
  x:               { label: 'X',         color: '#aaa'    },
  threads:         { label: 'Threads',   color: '#888'    },
  pinterest:       { label: 'Pinterest', color: '#e60023' },
  reddit:          { label: 'Reddit',    color: '#ff4500' },
  bluesky:         { label: 'Bluesky',   color: '#0085ff' },
};

// Post-Format Optionen
const POST_FORMATS = [
  { id: 'auto',        label: 'Automatisch',    icon: '✦',  hint: 'KI entscheidet Laenge und Stil frei.' },
  { id: 'reichweite',  label: 'Reichweite',     icon: '📈', hint: 'Plattformoptimierte Laengen fuer maximale organische Verbreitung.' },
  { id: 'Tipp',        label: 'Tipps & Tricks', icon: '💡', hint: 'Strukturierte Tipps, gut teilbar.' },
  { id: 'Meinung',     label: 'Meinung',        icon: '💬', hint: 'Klare Position, laedt zur Diskussion ein.' },
  { id: 'Frage',       label: 'Frage',          icon: '❓', hint: 'Community-Frage, hohes Kommentar-Potenzial.' },
  { id: 'Fallbeispiel',label: 'Fallbeispiel',   icon: '✅', hint: 'Konkretes Beispiel, hohe Glaubwuerdigkeit.' },
];

function Spinner({ color = 'var(--cyan)' }) {
  return <span style={{ width: 12, height: 12, border: `2px solid rgba(0,212,255,0.2)`, borderTopColor: color, borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block', flexShrink: 0 }} />;
}

// ── Haupt-Komponente ──────────────────────────────────────────────────────────
export default function Generate() {
  const { token, user } = useAuth();
  const location        = useLocation();
  const prefilled       = location.state?.prefillTopic || '';

  const [topic,              setTopic]             = useState(prefilled);
  const [postType,           setPostType]           = useState('auto');
  const [platforms,          setPlatforms]          = useState([]);
  const [result,             setResult]             = useState(null);
  const [loading,            setLoading]            = useState(false);
  const [step,               setStep]               = useState('idle');
  const [error,              setError]              = useState('');
  const [blogResult,         setBlogResult]         = useState(null);
  const [blogLoading,        setBlogLoading]        = useState(false);
  const [connectedPlatforms, setConnectedPlatforms] = useState(null);
  const [connectedAccounts,  setConnectedAccounts]  = useState([]);
  const [upConnected,        setUpConnected]        = useState(false);
  const [statusLoading,      setStatusLoading]      = useState(true);

  useEffect(() => {
    api('/uploadpost/status', {}, token)
      .then(data => {
        if (data.connected && data.platforms?.length > 0) {
          const raw = data.platforms.filter(p => p !== 'gbp' && p !== 'tiktok' && p !== 'youtube');
          setConnectedPlatforms(raw);
          setConnectedAccounts(data.accounts || []);
          setUpConnected(true);
        } else {
          setConnectedPlatforms(FALLBACK_PLATFORMS);
          setUpConnected(false);
        }
      })
      .catch(() => setConnectedPlatforms(FALLBACK_PLATFORMS))
      .finally(() => setStatusLoading(false));
    if (prefilled) window.history.replaceState({}, document.title);
  }, []);

  const availablePlatforms = ALL_PLATFORMS.filter(p => {
    if (!connectedPlatforms) return FALLBACK_PLATFORMS.includes(p.id);
    return connectedPlatforms.includes(p.id);
  });

  function togglePlatform(id) { setPlatforms(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); }
  function selectAll()  { setPlatforms(availablePlatforms.map(p => p.id)); }
  function selectNone() { setPlatforms([]); }

  async function handleGenerate() {
    if (!topic.trim()) { setError('Bitte ein Thema eingeben.'); return; }
    if (platforms.length === 0) { setError('Bitte mindestens eine Plattform auswaehlen.'); return; }
    setLoading(true); setError(''); setResult(null); setBlogResult(null); setStep('researching');
    try {
      await new Promise(r => setTimeout(r, 500));
      setStep('writing');
      const data = await api('/posts/generate', {
        method: 'POST',
        body: JSON.stringify({ topic: topic.trim(), platforms, postType: postType !== 'auto' ? postType : undefined })
      }, token);
      setResult(data); setStep('done');
    } catch (e) { setError(e.message); setStep('idle'); }
    finally { setLoading(false); }
  }

  async function handleBlog() {
    if (!topic.trim()) { setError('Bitte ein Thema eingeben.'); return; }
    setBlogLoading(true); setError(''); setBlogResult(null); setResult(null);
    try {
      const data = await api('/posts/blog', { method: 'POST', body: JSON.stringify({ topic: topic.trim() }) }, token);
      setBlogResult(data);
    } catch (e) { setError(e.message); }
    finally { setBlogLoading(false); }
  }

  const activeFormat = POST_FORMATS.find(f => f.id === postType);

  return (
    <div style={{ padding: '28px 32px', maxWidth: 880 }}>
      <div className="fade-up" style={{ marginBottom: 22 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <span className="dot-live" />
          <span style={{ fontSize: 10, color: 'var(--cyan)', fontFamily: 'Outfit', letterSpacing: '0.12em' }}>KI-CONTENT-GENERATOR</span>
        </div>
        <h1 style={{ fontSize: 26, fontWeight: 700, marginBottom: 4 }}>Post <span className="gradient-text">generieren</span></h1>
        <p style={{ color: 'var(--text2)', fontSize: 13 }}>KI recherchiert zuerst — dann schreibt sie in deinem Stil.</p>
        {prefilled && (
          <div style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, padding: '5px 12px', background: 'var(--cyan-dim)', border: '1px solid rgba(0,255,255,0.2)', borderRadius: 20 }}>
            <span style={{ fontSize: 11 }}>💡</span>
            <span style={{ fontSize: 11, color: 'var(--cyan)' }}>Idee aus Dashboard uebernommen</span>
          </div>
        )}
      </div>

      <div className="fade-up glass" style={{ padding: 22, marginBottom: 20, animationDelay: '0.05s' }}>

        {/* Thema */}
        <label style={{ display: 'block', marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 700, fontFamily: 'Outfit', color: 'var(--muted)', letterSpacing: '0.1em', marginBottom: 7 }}>◈ THEMA / IDEE</div>
          <textarea value={topic} onChange={e => setTopic(e.target.value)}
            placeholder="z.B. 5 Tipps fuer bessere Online-Sichtbarkeit oder: Warum Kunden nicht zurueckrufen"
            rows={3} className="input" style={{ resize: 'vertical', lineHeight: 1.65, fontSize: 13 }}
            onKeyDown={e => { if (e.key === 'Enter' && e.ctrlKey) handleGenerate(); }}
            autoFocus={!!prefilled}
          />
          <div style={{ fontSize: 10, color: 'var(--muted)', marginTop: 4 }}>Strg+Enter zum Generieren</div>
        </label>

        {/* Post-Format */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 10, fontWeight: 700, fontFamily: 'Outfit', color: 'var(--muted)', letterSpacing: '0.1em', marginBottom: 8 }}>◈ POST-FORMAT</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
            {POST_FORMATS.map(t => (
              <button key={t.id} onClick={() => setPostType(t.id)} style={{
                padding: '5px 12px', borderRadius: 8, fontSize: 12, cursor: 'pointer', fontFamily: 'Outfit',
                background: postType === t.id
                  ? t.id === 'reichweite' ? 'rgba(0,255,136,0.1)' : 'var(--cyan-dim)'
                  : 'transparent',
                color: postType === t.id
                  ? t.id === 'reichweite' ? 'var(--green)' : 'var(--cyan)'
                  : 'var(--text2)',
                border: `1px solid ${postType === t.id
                  ? t.id === 'reichweite' ? 'rgba(0,255,136,0.35)' : 'rgba(0,255,255,0.3)'
                  : 'var(--glass-border)'}`,
                transition: 'all 0.15s', display: 'flex', alignItems: 'center', gap: 5
              }}>{t.icon} {t.label}</button>
            ))}
          </div>
          {/* Format-Hinweis */}
          {activeFormat && (
            <div style={{ padding: '7px 12px', background: 'var(--glass2)', borderRadius: 8, border: '1px solid var(--glass-border)', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, flexShrink: 0 }}>{activeFormat.icon}</span>
              <span style={{ fontSize: 11, color: 'var(--muted)', lineHeight: 1.5 }}>
                <span style={{ color: 'var(--text2)', fontWeight: 600 }}>{activeFormat.label}:</span>{' '}{activeFormat.hint}
              </span>
            </div>
          )}
        </div>

        {/* Plattformen */}
        <div style={{ marginBottom: 20 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 }}>
            <div style={{ fontSize: 10, fontWeight: 700, fontFamily: 'Outfit', color: 'var(--muted)', letterSpacing: '0.1em' }}>
              ◈ PLATTFORMEN {platforms.length > 0 && <span style={{ color: 'var(--cyan)' }}>({platforms.length} gewaehlt)</span>}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {connectedPlatforms && (
                <div style={{ display: 'flex', gap: 5 }}>
                  <button onClick={selectAll}  className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }}>Alle</button>
                  <button onClick={selectNone} className="btn-ghost" style={{ fontSize: 10, padding: '3px 8px' }}>Keine</button>
                </div>
              )}
              {!statusLoading && (upConnected
                ? <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span className="dot-live" style={{ width: 5, height: 5 }} /><span style={{ fontSize: 10, color: 'var(--green)' }}>Upload-Post aktiv</span></div>
                : <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}><span style={{ fontSize: 10, color: 'var(--muted)' }}>Mehr:</span><Link to="/settings?tab=channels"><span style={{ fontSize: 10, color: 'var(--cyan)' }}>Kanäle verbinden →</span></Link></div>
              )}
            </div>
          </div>
          {statusLoading ? (
            <div className="shimmer" style={{ height: 44, borderRadius: 8 }} />
          ) : (
            <>
              <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap', marginBottom: platforms.length === 0 ? 8 : 0 }}>
                {availablePlatforms.map(p => {
                  const active  = platforms.includes(p.id);
                  const acc     = connectedAccounts.find(a => (a.platform || '').toLowerCase() === p.id);
                  const accName = acc ? (acc.display_name || acc.name || '') : '';
                  return (
                    <button key={p.id} onClick={() => togglePlatform(p.id)} style={{
                      display: 'flex', alignItems: 'center', gap: 7, padding: '7px 13px', borderRadius: 8, fontSize: 12, cursor: 'pointer',
                      border: `1px solid ${active ? p.color + '60' : 'var(--glass-border)'}`,
                      background: active ? `${p.color}12` : 'var(--card-bg)', color: active ? p.color : 'var(--text2)',
                      transition: 'all 0.15s', boxShadow: active ? `0 0 10px ${p.color}20` : 'none',
                      fontFamily: 'Outfit', fontWeight: active ? 600 : 400
                    }}>
                      <span style={{ width: 20, height: 20, borderRadius: 5, fontSize: 9, fontWeight: 800, background: active ? p.color : 'var(--glass-border)', color: active ? '#fff' : 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{p.short}</span>
                      <span>{p.label}</span>
                      {accName ? <span style={{ fontSize: 10, opacity: 0.75, maxWidth: 90, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{accName}</span>
                               : <span style={{ fontSize: 9, opacity: 0.5 }}>{p.desc}</span>}
                    </button>
                  );
                })}
              </div>
              {platforms.length === 0 && (
                <div style={{ padding: '8px 12px', background: 'rgba(255,170,0,0.06)', border: '1px solid rgba(255,170,0,0.18)', borderRadius: 7 }}>
                  <p style={{ fontSize: 12, color: 'var(--gold)' }}>Bitte mindestens eine Plattform auswaehlen.</p>
                </div>
              )}
            </>
          )}
        </div>

        {loading && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 8, background: 'rgba(0,180,220,0.06)', border: '1px solid var(--glass-border2)', marginBottom: 14 }}>
            <Spinner />
            <span style={{ fontSize: 12, color: 'var(--cyan)', fontFamily: 'Outfit' }}>
              {step === 'researching' ? '◈ RECHERCHE LÄUFT...' : '✦ POSTS WERDEN GESCHRIEBEN...'}
            </span>
          </div>
        )}
        {error && <div style={{ padding: '9px 13px', borderRadius: 8, marginBottom: 14, background: 'rgba(255,0,85,0.08)', border: '1px solid rgba(255,0,85,0.2)', color: 'var(--red)', fontSize: 12 }}>{error}</div>}

        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={handleGenerate} disabled={loading || blogLoading || !topic.trim() || platforms.length === 0} className="btn-primary"
            style={{ flex: 1, padding: '12px', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, fontFamily: 'Outfit', letterSpacing: '0.05em', opacity: platforms.length === 0 ? 0.5 : 1 }}>
            {loading ? <><Spinner /> VERARBEITE...</> : <>✦ POSTS GENERIEREN</>}
          </button>
          <button onClick={handleBlog} disabled={loading || blogLoading || !topic.trim()} className="btn-ghost"
            style={{ padding: '12px 20px', fontSize: 14, display: 'flex', alignItems: 'center', gap: 8, fontFamily: 'Outfit', letterSpacing: '0.03em', whiteSpace: 'nowrap', border: '1px solid rgba(200,164,90,0.4)', color: 'var(--gold)' }}>
            {blogLoading ? <><Spinner color="var(--gold)" /> BLOG...</> : <>📝 BLOG</>}
          </button>
        </div>
      </div>

      {result && <PostResult post={result} topic={topic} token={token} userTier={user?.tier || 'basic'} connectedPlatforms={connectedPlatforms || FALLBACK_PLATFORMS} />}
      {blogResult && <BlogResult blog={blogResult} />}
    </div>
  );
}

// ── PostResult ────────────────────────────────────────────────────────────────
function PostResult({ post, topic, token, userTier, connectedPlatforms }) {
  const [variants,      setVariants]      = useState(post.variants || []);
  const [showPublish,   setShowPublish]   = useState(false);
  const [showImgPanel,  setShowImgPanel]  = useState(false);
  const [postImageData, setPostImageData] = useState(null);

  const publishablePlatforms = variants.map(v => v.platform).filter(p => (connectedPlatforms || []).includes(p));
  const canPublish            = publishablePlatforms.length > 0;
  const firstImageBrief       = variants.find(v => v.image_brief)?.image_brief || null;

  async function toggleApprove(v) {
    try {
      const u = await api(`/posts/variants/${v.id}`, { method: 'PATCH', body: JSON.stringify({ approved: !v.approved }) }, token);
      setVariants(p => p.map(x => x.id === u.id ? { ...x, ...u } : x));
    } catch {}
  }
  async function saveEdit(v, content) {
    try {
      const u = await api(`/posts/variants/${v.id}`, { method: 'PATCH', body: JSON.stringify({ content }) }, token);
      setVariants(p => p.map(x => x.id === u.id ? { ...x, ...u } : x));
    } catch {}
  }

  return (
    <div className="fade-up">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <span className="dot-live" />
          <span style={{ fontSize: 9, fontWeight: 700, fontFamily: 'Outfit', color: 'var(--muted)', letterSpacing: '0.12em' }}>GENERIERTE POSTS — {variants.length} VARIANTEN</span>
        </div>
        <button onClick={() => setShowImgPanel(p => !p)} style={{
          fontSize: 12, padding: '7px 16px', borderRadius: 8, cursor: 'pointer', fontWeight: 500,
          background: showImgPanel ? 'var(--cyan-dim)' : postImageData ? 'rgba(0,212,255,0.08)' : 'var(--glass)',
          color: showImgPanel ? 'var(--cyan)' : postImageData ? 'var(--cyan)' : 'var(--text2)',
          border: `1px solid ${showImgPanel ? 'rgba(0,255,255,0.3)' : postImageData ? 'rgba(0,212,255,0.2)' : 'var(--glass-border)'}`,
          display: 'flex', alignItems: 'center', gap: 6,
        }}>
          {postImageData ? '✓ Bild fuer alle Posts' : '🖼 Bild erstellen'}
        </button>
      </div>

      {showImgPanel && (
        <div style={{ marginBottom: 16 }}>
          <PostImagePanel postId={post.id} topic={topic} imageBrief={firstImageBrief}
            firstPlatform={variants[0]?.platform || 'instagram'} firstVariantId={variants[0]?.id}
            token={token} userTier={userTier} imageData={postImageData} savedImage={null}
            onImageChange={setPostImageData} onClose={() => setShowImgPanel(false)} />
        </div>
      )}

      {postImageData && !showImgPanel && (
        <div className="fade-in glass" style={{ marginBottom: 14, padding: '10px 14px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src={postImageData} alt="Post-Bild" style={{ width: 56, height: 56, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--glass-border2)', flexShrink: 0 }} />
          <div style={{ flex: 1 }}>
            <p style={{ fontSize: 12, color: 'var(--green)', marginBottom: 2 }}>✓ Bild wird fuer alle Plattformen verwendet</p>
            <p style={{ fontSize: 11, color: 'var(--muted)' }}>Beim Veroeffentlichen automatisch eingesetzt</p>
          </div>
          <button onClick={() => setShowImgPanel(true)} className="btn-ghost" style={{ fontSize: 11 }}>↻ Ändern</button>
        </div>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {variants.map(v => (
          <VariantCard key={v.id} variant={v} token={token} onApprove={() => toggleApprove(v)} onSave={c => saveEdit(v, c)} />
        ))}
      </div>

      {canPublish && (
        <div className="fade-up" style={{ marginTop: 20 }}>
          {!showPublish
            ? <button onClick={() => setShowPublish(true)} className="btn-primary" style={{ width: '100%', padding: '13px', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}>⊹ Veroeffentlichen</button>
            : <GeneratePublishPanel post={{ ...post, variants }} token={token} connectedPlatforms={connectedPlatforms || []}
                publishablePlatforms={publishablePlatforms} sharedImage={postImageData}
                onVariantsApproved={updated => setVariants(prev => prev.map(v => { const u = updated.find(x => x.id === v.id); return u ? { ...v, ...u } : v; }))}
                onClose={() => setShowPublish(false)} />}
        </div>
      )}
      {!canPublish && connectedPlatforms?.length === 0 && (
        <div style={{ marginTop: 16, padding: '10px 14px', background: 'rgba(255,170,0,0.06)', border: '1px solid rgba(255,170,0,0.2)', borderRadius: 10, textAlign: 'center' }}>
          <p style={{ fontSize: 12, color: 'var(--gold)' }}>Kanäle verbinden →<Link to="/settings?tab=channels" style={{ color: 'var(--cyan)', marginLeft: 6 }}>Einstellungen</Link></p>
        </div>
      )}
    </div>
  );
}

// ── Bild-Panel ────────────────────────────────────────────────────────────────
function PostImagePanel({ postId, topic, imageBrief, firstPlatform, firstVariantId, token, userTier, imageData, savedImage, onImageChange, onClose }) {
  const [imageStyle,      setImageStyle]      = useState('professional');
  const [imageProvider,   setImageProvider]   = useState('gemini');
  const [applyLogo,       setApplyLogo]       = useState(true);
  const [companyLogo,     setCompanyLogo]     = useState(null);
  const [customPrompt,    setCustomPrompt]    = useState('');
  const [showCustom,      setShowCustom]      = useState(false);
  const [optimizedPrompt, setOptimizedPrompt] = useState('');
  const [loading,         setLoading]         = useState(false);
  const [optimizing,      setOptimizing]      = useState(false);
  const [uploading,       setUploading]       = useState(false);
  const [error,           setError]           = useState('');
  const fileInputRef = useRef(null);
  const currentImage = imageData || (savedImage?.image_data) || null;

  useEffect(() => {
    api('/company', {}, token).then(d => setCompanyLogo(d?.logo_data || false)).catch(() => setCompanyLogo(false));
  }, []);

  async function handleGenerate(customPromptToUse = null) {
    setLoading(true); setError('');
    try {
      const body = {
        topic, image_brief: imageBrief || undefined, platform: firstPlatform,
        style: imageStyle, variant_id: firstVariantId, post_id: postId,
        provider: imageProvider, apply_logo: applyLogo && !!companyLogo,
      };
      if (customPromptToUse) body.custom_prompt = customPromptToUse;
      const data = await api('/images/generate', { method: 'POST', body: JSON.stringify(body) }, token);
      onImageChange(data.image);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }

  async function handleOptimizeAndGenerate() {
    if (!customPrompt.trim()) return;
    setOptimizing(true); setError(''); setOptimizedPrompt('');
    try {
      const d = await api('/images/optimize-prompt', { method: 'POST', body: JSON.stringify({ user_prompt: customPrompt, topic, platform: firstPlatform, style: imageStyle }) }, token);
      setOptimizedPrompt(d.optimized_prompt);
      await handleGenerate(d.optimized_prompt);
    } catch (e) { setError(e.message); }
    finally { setOptimizing(false); }
  }

  async function handleUpload(e) {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('image', file); fd.append('variant_id', firstVariantId || ''); fd.append('platform', firstPlatform || 'instagram');
      const res  = await fetch('/api/images/upload', { method: 'POST', headers: { Authorization: `Bearer ${token}` }, body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Upload fehlgeschlagen');
      onImageChange(data.image);
    } catch (e) { setError(e.message); }
    finally { setUploading(false); e.target.value = ''; }
  }

  const canFlux = ['team','agency'].includes(userTier);

  return (
    <div className="fade-in glass" style={{ overflow: 'hidden' }}>
      <div style={{ height: 2, background: 'linear-gradient(90deg, var(--cyan), var(--blue))' }} />
      <div style={{ padding: '14px 18px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 10, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.1em' }}>◈ BILD FÜR ALLE POSTS</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>

        {imageBrief && (
          <div style={{ marginBottom: 12, padding: '8px 12px', background: 'rgba(0,212,255,0.05)', border: '1px solid rgba(0,212,255,0.15)', borderRadius: 8 }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.08em', marginBottom: 4 }}>◈ KI-BILD-BRIEFING</div>
            <p style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>{imageBrief}</p>
          </div>
        )}

        <div style={{ marginBottom: 12 }}>
          <div style={{ fontSize: 9, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 6 }}>KI-MODELL</div>
          <div style={{ display: 'flex', gap: 6 }}>
            {[{ id: 'gemini', label: '✨ Gemini', desc: 'Schnell' }, { id: 'fal', label: '⚡ Flux Pro', desc: 'Qualität', pro: true }].map(prov => {
              const locked = prov.pro && !canFlux;
              return (
                <button key={prov.id} onClick={() => !locked && setImageProvider(prov.id)} style={{
                  padding: '6px 14px', borderRadius: 7, fontSize: 11, cursor: locked ? 'default' : 'pointer', flex: 1, opacity: locked ? 0.4 : 1,
                  background: imageProvider === prov.id ? (prov.id === 'fal' ? 'rgba(200,164,90,0.12)' : 'var(--cyan-dim)') : 'transparent',
                  color: imageProvider === prov.id ? (prov.id === 'fal' ? 'var(--gold)' : 'var(--cyan)') : 'var(--text2)',
                  border: `1px solid ${imageProvider === prov.id ? (prov.id === 'fal' ? 'rgba(200,164,90,0.4)' : 'rgba(0,255,255,0.3)') : 'var(--glass-border)'}`,
                }}>
                  <div style={{ fontWeight: 600 }}>{prov.label}</div>
                  <div style={{ fontSize: 9, opacity: 0.7 }}>{locked ? 'Team/Agency' : prov.desc}</div>
                </button>
              );
            })}
          </div>
        </div>

        {companyLogo && (
          <div style={{ marginBottom: 12, padding: '8px 10px', background: 'var(--glass2)', borderRadius: 8, border: '1px solid var(--glass-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <img src={companyLogo} alt="Logo" style={{ height: 22, maxWidth: 60, objectFit: 'contain' }} />
              <span style={{ fontSize: 11, color: 'var(--text2)' }}>Logo einblenden</span>
            </div>
            <button onClick={() => setApplyLogo(v => !v)} style={{ width: 36, height: 20, borderRadius: 10, cursor: 'pointer', border: 'none', background: applyLogo ? 'var(--green)' : 'var(--glass-border2)', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
              <span style={{ position: 'absolute', top: 2, left: applyLogo ? 18 : 2, width: 16, height: 16, borderRadius: '50%', background: '#fff', transition: 'left 0.2s' }} />
            </button>
          </div>
        )}

        <div style={{ display: 'flex', gap: 6, marginBottom: 12, flexWrap: 'wrap' }}>
          {IMAGE_STYLES.map(s => (
            <button key={s.id} onClick={() => setImageStyle(s.id)} style={{ padding: '4px 10px', borderRadius: 7, fontSize: 11, cursor: 'pointer', background: imageStyle === s.id ? 'var(--cyan-dim)' : 'transparent', color: imageStyle === s.id ? 'var(--cyan)' : 'var(--text2)', border: `1px solid ${imageStyle === s.id ? 'rgba(0,255,255,0.25)' : 'var(--glass-border)'}` }}>{s.icon} {s.label}</button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12, alignItems: 'center' }}>
          <button onClick={() => handleGenerate()} disabled={loading || optimizing} className="btn-primary" style={{ fontSize: 12, padding: '8px 18px' }}>
            {loading && !optimizing ? <><Spinner /> Generiert...</> : '🖼 KI-Bild erstellen'}
          </button>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={handleUpload} style={{ display: 'none' }} />
          <button onClick={() => fileInputRef.current?.click()} disabled={uploading} className="btn-ghost" style={{ fontSize: 11 }}>
            {uploading ? <><Spinner /> Lädt...</> : '📁 Bild hochladen'}
          </button>
          <button onClick={() => setShowCustom(v => !v)} className="btn-ghost" style={{ fontSize: 11, color: showCustom ? 'var(--cyan)' : undefined }}>🎨 Eigener Prompt</button>
          {currentImage && <button onClick={() => onImageChange(null)} className="btn-ghost" style={{ fontSize: 11, marginLeft: 'auto' }}>✕ Bild entfernen</button>}
        </div>

        {showCustom && (
          <div className="fade-in" style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--glass2)', borderRadius: 9, border: '1px solid var(--glass-border)' }}>
            <div style={{ fontSize: 10, color: 'var(--cyan)', fontWeight: 600, letterSpacing: '0.08em', marginBottom: 8 }}>EIGENER PROMPT — CLAUDE OPTIMIERT AUTOMATISCH</div>
            <textarea value={customPrompt} onChange={e => setCustomPrompt(e.target.value)} placeholder="z.B. Moderner Buero-Schreibtisch von oben, warme Beleuchtung, minimalistisch" rows={3} className="input" style={{ fontSize: 12, resize: 'vertical', marginBottom: 8 }} />
            {optimizedPrompt && (
              <div style={{ padding: '8px 10px', background: 'rgba(0,255,255,0.06)', border: '1px solid rgba(0,255,255,0.2)', borderRadius: 7, marginBottom: 8 }}>
                <div style={{ fontSize: 9, color: 'var(--cyan)', letterSpacing: '0.08em', marginBottom: 4 }}>OPTIMIERTER PROMPT</div>
                <p style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.5 }}>{optimizedPrompt}</p>
              </div>
            )}
            <button onClick={handleOptimizeAndGenerate} disabled={optimizing || loading || !customPrompt.trim()} className="btn-primary" style={{ fontSize: 11, padding: '6px 16px' }}>
              {optimizing ? <><Spinner /> Optimiert &amp; generiert...</> : '✦ Prompt optimieren &amp; Bild erstellen'}
            </button>
          </div>
        )}

        {error && <div style={{ marginBottom: 10, fontSize: 11, color: 'var(--red)', padding: '7px 10px', background: 'rgba(255,0,85,0.08)', borderRadius: 7 }}>{error}</div>}

        {currentImage && (
          <div className="fade-up">
            <img src={currentImage} alt="Post-Bild" style={{ width: '100%', maxHeight: 280, objectFit: 'contain', borderRadius: 9, border: '1px solid var(--glass-border2)', marginBottom: 8, background: 'var(--glass2)' }} />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={() => { const a = document.createElement('a'); a.href = currentImage; a.download = 'bild.png'; a.click(); }} className="btn-primary" style={{ fontSize: 11, padding: '6px 14px' }}>⬇ Herunterladen</button>
              <button onClick={() => handleGenerate()} disabled={loading} className="btn-ghost" style={{ fontSize: 11 }}>↻ Neu generieren</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Blog-Ergebnis ─────────────────────────────────────────────────────────────
function BlogResult({ blog }) {
  const [copied, setCopied] = useState(false);
  function handleCopy() { navigator.clipboard.writeText(blog.content || ''); setCopied(true); setTimeout(() => setCopied(false), 2500); }
  return (
    <div className="fade-up glass" style={{ overflow: 'hidden', marginTop: 20 }}>
      <div style={{ height: 2, background: 'linear-gradient(90deg, var(--gold), transparent)' }} />
      <div style={{ padding: '16px 20px', borderBottom: '1px solid var(--glass-border)', background: 'var(--glass)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 16 }}>📝</span>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>{blog.title}</div>
            <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 2 }}>{blog.reading_time && `⏱ ${blog.reading_time}`}{blog.word_count && ` · ca. ${blog.word_count} Wörter`}</div>
          </div>
        </div>
        <button onClick={handleCopy} className="btn-primary" style={{ fontSize: 12, padding: '7px 16px', background: copied ? 'rgba(0,255,136,0.15)' : undefined, color: copied ? 'var(--green)' : undefined }}>
          {copied ? '✓ Kopiert!' : '⎘ Inhalt kopieren'}
        </button>
      </div>
      {blog.meta_description && (
        <div style={{ padding: '10px 20px', background: 'rgba(200,164,90,0.06)', borderBottom: '1px solid rgba(200,164,90,0.15)' }}>
          <span style={{ fontSize: 10, fontWeight: 600, color: 'var(--gold)', letterSpacing: '0.08em', marginRight: 8 }}>SEO META</span>
          <span style={{ fontSize: 12, color: 'var(--text2)' }}>{blog.meta_description}</span>
        </div>
      )}
      <div style={{ padding: '20px' }}>
        <pre style={{ fontSize: 13, lineHeight: 1.85, color: 'var(--text2)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'inherit', margin: 0 }}>{blog.content}</pre>
      </div>
    </div>
  );
}

// ── Publish Panel ─────────────────────────────────────────────────────────────
function GeneratePublishPanel({ post, token, connectedPlatforms, publishablePlatforms, sharedImage, onVariantsApproved, onClose }) {
  const textPlats = publishablePlatforms.filter(p => TEXT_PLATS.includes(p));
  const [selectedPlats,    setSelectedPlats]    = useState(publishablePlatforms);
  const [mode,             setMode]             = useState('now');
  const [scheduleDate,     setScheduleDate]     = useState('');
  const [scheduleTime,     setScheduleTime]     = useState('09:00');
  const [noImagePlatforms, setNoImagePlatforms] = useState([]);
  const [publishing,       setPublishing]       = useState(false);
  const [result,           setResult]           = useState(null);
  const [error,            setError]            = useState('');

  const minDate    = new Date(Date.now() + 10 * 60000).toISOString().slice(0, 10);
  const gbpVariant = post.variants?.find(v => v.platform === 'google_business');
  const gbpText    = gbpVariant ? gbpVariant.content.replace(/\s*#\w+/g, '').trim() : '';
  const gbpTooLong = selectedPlats.includes('google_business') && gbpText.length > 1500;
  const canPublish = selectedPlats.length > 0 && !gbpTooLong && (mode === 'now' || (mode === 'later' && scheduleDate));

  function togglePlatform(id) { setSelectedPlats(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); }
  function toggleNoImage(id)  { setNoImagePlatforms(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]); }

  async function handlePublish() {
    setPublishing(true); setError('');
    const scheduleAt = mode === 'later' && scheduleDate ? `${scheduleDate}T${scheduleTime}:00` : null;
    try {
      const toApprove = post.variants?.filter(v => selectedPlats.includes(v.platform) && !v.approved) || [];
      const approved  = [];
      for (const v of toApprove) {
        const u = await api(`/posts/variants/${v.id}`, { method: 'PATCH', body: JSON.stringify({ approved: true }) }, token);
        approved.push(u);
      }
      if (approved.length > 0 && onVariantsApproved) onVariantsApproved(approved);
      const res = await api('/uploadpost/publish-post', { method: 'POST', body: JSON.stringify({ post_id: post.id, platforms: selectedPlats, schedule_at: scheduleAt, image_data: sharedImage || undefined, no_image_platforms: noImagePlatforms }) }, token);
      setResult({ scheduled: !!scheduleAt, scheduleAt, results: res.results || [], warnings: res.warnings || [], autoApproved: toApprove.length });
    } catch (err) { setError(err.message); }
    finally { setPublishing(false); }
  }

  if (result) {
    const succeeded = result.results.filter(r => r.success);
    const failed    = result.results.filter(r => !r.success && !r.skipped);
    return (
      <div className="fade-in glass" style={{ overflow: 'hidden' }}>
        <div style={{ height: 2, background: failed.length === 0 ? 'var(--green)' : 'var(--gold)' }} />
        <div style={{ padding: '16px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span className="dot-live" />
                <span style={{ fontSize: 13, color: 'var(--green)', fontWeight: 600 }}>
                  {result.scheduled ? `📅 Geplant — ${new Date(result.scheduleAt).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} Uhr` : `✓ Veroeffentlicht auf ${succeeded.length} Plattform${succeeded.length !== 1 ? 'en' : ''}`}
                </span>
              </div>
              {result.autoApproved > 0 && <p style={{ fontSize: 11, color: 'var(--muted)', marginBottom: 8 }}>ⓘ {result.autoApproved} Variante{result.autoApproved !== 1 ? 'n' : ''} automatisch freigegeben</p>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                {result.results.map(r => { const pl = PLAT_INFO[r.platform] || { label: r.platform, color: '#888' }; return <span key={r.platform} style={{ fontSize: 11, padding: '3px 10px', borderRadius: 20, fontWeight: 600, background: r.success ? `${pl.color}18` : 'var(--red-dim)', color: r.success ? pl.color : 'var(--red)', border: `1px solid ${r.success ? pl.color + '40' : 'rgba(255,0,85,0.3)'}` }}>{r.success ? '✓ ' : '✕ '}{pl.label}</span>; })}
              </div>
              {result.warnings?.length > 0 && <div style={{ padding: '6px 10px', background: 'rgba(255,170,0,0.08)', border: '1px solid rgba(255,170,0,0.25)', borderRadius: 7, marginBottom: 6 }}>{result.warnings.map((w, i) => <p key={i} style={{ fontSize: 11, color: 'var(--gold)' }}>⚠ {w}</p>)}</div>}
              {failed.length > 0 && <div style={{ padding: '6px 10px', background: 'rgba(255,0,85,0.06)', border: '1px solid rgba(255,0,85,0.2)', borderRadius: 7 }}>{failed.map(r => <p key={r.platform} style={{ fontSize: 11, color: 'var(--red)' }}>{PLAT_INFO[r.platform]?.label || r.platform}: {r.error}</p>)}</div>}
              <Link to="/history" style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 10, fontSize: 12, color: 'var(--cyan)' }}>◷ Zu Meine Posts →</Link>
            </div>
            <button onClick={onClose} className="btn-ghost" style={{ fontSize: 11, flexShrink: 0 }}>✕</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in glass" style={{ overflow: 'hidden' }}>
      <div style={{ height: 2, background: 'linear-gradient(90deg, var(--cyan), var(--blue))' }} />
      <div style={{ padding: '16px 18px 0' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14 }}>
          <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.1em' }}>⊹ VEROEFFENTLICHEN</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 16 }}>✕</button>
        </div>
        <div style={{ padding: '7px 11px', background: 'rgba(0,255,255,0.05)', border: '1px solid rgba(0,255,255,0.15)', borderRadius: 8, marginBottom: 14 }}>
          <p style={{ fontSize: 11, color: 'var(--cyan)' }}>ⓘ Nicht freigegebene Texte werden beim Veroeffentlichen automatisch freigegeben.</p>
        </div>
        {sharedImage && <div style={{ padding: '8px 11px', background: 'rgba(0,255,136,0.05)', border: '1px solid rgba(0,255,136,0.2)', borderRadius: 8, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 10 }}><img src={sharedImage} alt="" style={{ width: 36, height: 36, objectFit: 'cover', borderRadius: 6, flexShrink: 0 }} /><p style={{ fontSize: 11, color: 'var(--green)' }}>✓ Bild wird fuer alle Plattformen verwendet</p></div>}
        {gbpTooLong && <div style={{ padding: '7px 11px', background: 'rgba(255,0,85,0.06)', border: '1px solid rgba(255,0,85,0.3)', borderRadius: 8, marginBottom: 12 }}><p style={{ fontSize: 11, color: 'var(--red)' }}>✕ Google Business Text zu lang ({gbpText.length}/1500). Bitte bearbeiten oder Google abwaehlen.</p></div>}
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>1 · PLATTFORMEN</div>
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {publishablePlatforms.map(id => { const pl = PLAT_INFO[id] || { label: id, color: '#888' }; const active = selectedPlats.includes(id); return <button key={id} onClick={() => togglePlatform(id)} style={{ padding: '6px 14px', borderRadius: 8, fontSize: 12, cursor: 'pointer', background: active ? `${pl.color}18` : 'var(--glass)', color: active ? pl.color : 'var(--muted)', border: `1px solid ${active ? pl.color + '55' : 'var(--glass-border)'}`, fontWeight: active ? 700 : 400 }}>{active ? '✓ ' : ''}{pl.label}</button>; })}
          </div>
        </div>
        {sharedImage && textPlats.filter(p => selectedPlats.includes(p)).length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>2 · OHNE BILD POSTEN</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {textPlats.filter(p => selectedPlats.includes(p)).map(id => { const pl = PLAT_INFO[id] || { label: id, color: '#888' }; const off = noImagePlatforms.includes(id); return <button key={id} onClick={() => toggleNoImage(id)} style={{ padding: '3px 10px', borderRadius: 6, fontSize: 11, cursor: 'pointer', background: off ? 'var(--glass2)' : `${pl.color}18`, color: off ? 'var(--muted)' : pl.color, border: `1px solid ${off ? 'var(--glass-border)' : pl.color + '40'}`, textDecoration: off ? 'line-through' : 'none' }}>{off ? '○' : '🖼'} {pl.label}</button>; })}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 8 }}>{sharedImage ? '3' : '2'} · WANN</div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
            {[['now','⊹','Jetzt sofort','var(--green-dim)','rgba(0,255,136,0.5)','var(--green)'],['later','📅','Geplant','var(--cyan-dim)','rgba(0,229,255,0.5)','var(--cyan)']].map(([m,ic,t,bg,bc,c]) => (
              <button key={m} onClick={() => setMode(m)} style={{ padding: '10px 14px', borderRadius: 10, cursor: 'pointer', background: mode===m ? bg : 'var(--glass)', border: `2px solid ${mode===m ? bc : 'var(--glass-border)'}`, color: mode===m ? c : 'var(--muted)', display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 16 }}>{ic}</span><span style={{ fontSize: 13, fontWeight: 600 }}>{t}</span>
              </button>
            ))}
          </div>
          {mode === 'later' && (
            <div className="fade-in" style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap', padding: '10px 12px', background: 'var(--glass)', borderRadius: 9, border: '1px solid var(--glass-border2)' }}>
              <div style={{ flex: 1, minWidth: 140 }}>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>DATUM</div>
                <input type="date" value={scheduleDate} onChange={e => setScheduleDate(e.target.value)} min={minDate} style={{ width: '100%', padding: '8px 10px', background: 'var(--glass2)', border: '1px solid var(--glass-border2)', borderRadius: 8, color: 'var(--text)', fontSize: 13, colorScheme: 'auto', boxSizing: 'border-box' }} />
              </div>
              <div style={{ width: 120 }}>
                <div style={{ fontSize: 10, color: 'var(--muted)', marginBottom: 4 }}>UHRZEIT</div>
                <input type="time" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)} style={{ width: '100%', padding: '8px 10px', background: 'var(--glass2)', border: '1px solid var(--glass-border2)', borderRadius: 8, color: 'var(--text)', fontSize: 13, colorScheme: 'auto', boxSizing: 'border-box' }} />
              </div>
              {scheduleDate && <div style={{ width: '100%', fontSize: 12, color: 'var(--cyan)', marginTop: 2 }}>📅 {new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' })} Uhr</div>}
            </div>
          )}
        </div>
      </div>
      <div style={{ padding: '0 18px 16px' }}>
        {error && <div style={{ padding: '8px 12px', background: 'rgba(255,0,85,0.06)', border: '1px solid rgba(255,0,85,0.3)', borderRadius: 8, marginBottom: 10 }}><p style={{ color: 'var(--red)', fontSize: 12 }}>{error}</p></div>}
        <button onClick={handlePublish} disabled={publishing || !canPublish} className="btn-primary" style={{ width: '100%', padding: '12px', fontSize: 14, fontWeight: 700, opacity: canPublish ? 1 : 0.4 }}>
          {publishing ? <><Spinner /> Wird veroeffentlicht...</> :
           mode === 'later' && scheduleDate
             ? `📅 Planen — ${new Date(`${scheduleDate}T${scheduleTime}`).toLocaleString('de-DE', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} Uhr · ${selectedPlats.length} Plattform${selectedPlats.length !== 1 ? 'en' : ''}`
             : `⊹ Jetzt veroeffentlichen · ${selectedPlats.length} Plattform${selectedPlats.length !== 1 ? 'en' : ''}`}
        </button>
      </div>
    </div>
  );
}

// ── VariantCard ───────────────────────────────────────────────────────────────
function VariantCard({ variant, token, onApprove, onSave }) {
  const [editing,   setEditing]   = useState(false);
  const [content,   setContent]   = useState(variant.content);
  const [copied,    setCopied]    = useState(false);
  const [showBrief, setShowBrief] = useState(false);

  const ALL_PLAT = {
    facebook:        { label: 'Facebook',  color: '#1877f2', short: 'f'  },
    instagram:       { label: 'Instagram', color: '#e1306c', short: '◈'  },
    linkedin:        { label: 'LinkedIn',  color: '#0077b5', short: 'in' },
    google_business: { label: 'Google',    color: '#ea4335', short: 'G'  },
    x:               { label: 'X',         color: '#555',    short: 'X'  },
    threads:         { label: 'Threads',   color: '#888',    short: '@'  },
    pinterest:       { label: 'Pinterest', color: '#e60023', short: 'P'  },
    reddit:          { label: 'Reddit',    color: '#ff4500', short: 'R'  },
    bluesky:         { label: 'Bluesky',   color: '#0085ff', short: '☁'  },
  };
  const p = ALL_PLAT[variant.platform] || { label: variant.platform, color: '#888', short: '?' };

  let hashtags = [];
  if (variant.hashtags) {
    try {
      const parsed = typeof variant.hashtags === 'string' ? JSON.parse(variant.hashtags) : variant.hashtags;
      hashtags = Array.isArray(parsed) ? parsed : [];
    } catch {}
  }

  useEffect(() => { if (!editing) setContent(variant.content); }, [variant.content, editing]);

  function handleCopy() {
    const textToCopy = hashtags.length > 0 ? variant.content + '\n\n' + hashtags.join(' ') : variant.content;
    navigator.clipboard.writeText(textToCopy);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }
  function handleSave()   { onSave(content); setEditing(false); }
  function handleCancel() { setContent(variant.content); setEditing(false); }

  return (
    <div className="glass" style={{ overflow: 'hidden', border: `1px solid ${variant.approved ? 'rgba(0,255,136,0.25)' : 'var(--glass-border2)'}` }}>
      <div style={{ height: 2, background: variant.approved ? 'var(--green)' : `linear-gradient(90deg, ${p.color}80, transparent)` }} />

      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, padding: '11px 16px', borderBottom: '1px solid var(--glass-border)', background: 'var(--glass)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <span style={{ width: 24, height: 24, borderRadius: 6, fontSize: 9, fontWeight: 800, background: `${p.color}20`, color: p.color, display: 'flex', alignItems: 'center', justifyContent: 'center', border: `1px solid ${p.color}30` }}>{p.short}</span>
          <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'Outfit', color: 'var(--text)' }}>{p.label}</span>
          {variant.image_brief && (
            <button onClick={() => setShowBrief(v => !v)} title="Bild-Briefing anzeigen" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, opacity: showBrief ? 1 : 0.45, padding: 0 }}>🎨</button>
          )}
        </div>
        <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
          {!editing ? (
            <>
              <button className="btn-ghost" onClick={() => setEditing(true)} style={{ fontSize: 11 }}>✎ Bearbeiten</button>
              <button className="btn-ghost" onClick={handleCopy} style={{ fontSize: 11, color: copied ? 'var(--green)' : undefined }}>{copied ? '✓ Kopiert' : '⎘ Kopieren'}</button>
            </>
          ) : (
            <>
              <button className="btn-ghost" onClick={handleSave} style={{ fontSize: 11, color: 'var(--cyan)' }}>✓ Speichern</button>
              <button className="btn-ghost" onClick={handleCancel} style={{ fontSize: 11 }}>✕ Abbrechen</button>
            </>
          )}
          <button onClick={onApprove} style={{
            fontSize: 11, fontWeight: 700, padding: '5px 12px', borderRadius: 7, cursor: 'pointer', fontFamily: 'Outfit',
            background: variant.approved ? 'rgba(0,255,136,0.1)' : 'transparent',
            color: variant.approved ? 'var(--green)' : 'var(--text2)',
            border: `1px solid ${variant.approved ? 'rgba(0,255,136,0.3)' : 'var(--glass-border)'}`,
          }}>
            {variant.approved ? '✓ FREIGEGEBEN' : 'FREIGEBEN'}
          </button>
        </div>
      </div>

      {showBrief && variant.image_brief && (
        <div className="fade-in" style={{ padding: '8px 16px', background: 'rgba(200,164,90,0.04)', borderBottom: '1px solid rgba(200,164,90,0.1)' }}>
          <span style={{ fontSize: 9, fontWeight: 700, color: 'var(--gold)', letterSpacing: '0.08em', marginRight: 8 }}>🎨 BILD-BRIEFING</span>
          <span style={{ fontSize: 11, color: 'var(--text2)' }}>{variant.image_brief}</span>
        </div>
      )}

      <div style={{ padding: '16px 18px' }}>
        {editing ? (
          <>
            <textarea value={content} onChange={e => setContent(e.target.value)}
              rows={Math.max(8, content.split('\n').length + 2)}
              className="input" style={{ lineHeight: 1.75, fontSize: 13, resize: 'vertical' }} autoFocus />
            <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
              <button onClick={handleSave} className="btn-primary" style={{ fontSize: 12, padding: '7px 18px' }}>Speichern</button>
              <button onClick={handleCancel} className="btn-ghost" style={{ fontSize: 12 }}>Abbrechen</button>
            </div>
          </>
        ) : (
          <p style={{ fontSize: 13, lineHeight: 1.85, color: 'var(--text2)', whiteSpace: 'pre-wrap' }}>{variant.content}</p>
        )}

        {!editing && hashtags.length > 0 && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--glass-border)' }}>
            <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--muted)', letterSpacing: '0.08em', marginBottom: 6 }}>HASHTAGS</div>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {hashtags.map((tag, i) => (
                <span key={i} style={{ fontSize: 11, padding: '2px 8px', borderRadius: 12, background: `${p.color}15`, color: p.color, border: `1px solid ${p.color}30` }}>{tag}</span>
              ))}
            </div>
          </div>
        )}

        {!editing && variant.posting_tip && (
          <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--glass-border)', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
            <span style={{ fontSize: 13, flexShrink: 0, marginTop: 1 }}>⏰</span>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, color: 'var(--cyan)', letterSpacing: '0.08em', marginBottom: 3 }}>BESTER ZEITPUNKT</div>
              <p style={{ fontSize: 11, color: 'var(--text2)', lineHeight: 1.55 }}>{variant.posting_tip}</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
